/* ════════════════════════════════════════════════════════════════════
   neoCheckerProtokoll.js
   Frontend für den Änderungsprotokoll-Spoiler im neoChecker-Widget.
   DOM-Zugriffe sind auf das Widget-Wurzelelement (rootEl) beschränkt.
   Formatierung/ID-Auflösung liegt in common/functions/protokollHelper.js,
   die Werte kommen aus stammdatenService und basedataService.

   Verwendung in neoChecker.js  →  siehe Protokoll-Integration.md
   ════════════════════════════════════════════════════════════════════ */
import stammdatenService from "./stammdatenService.js";
import basedataService from "./basedataService.js";
import { istSichtbar, buildCrumbs, esc, fmtValue, resolveValue } from "../../../../../common/functions/protokollHelper.js";

/* ── HTML-Markup für das Widget-Template (in den Konstruktor einsetzen) ── */
export const PROTOKOLL_MARKUP = `
  <div class="todo-protokoll" data-protokoll>
    <button class="np-toggle" data-np-toggle aria-expanded="false">
      <i class="bi bi-clock-history np-tico"></i>
      <span class="np-label" data-np-label>Änderungsprotokoll anzeigen</span>
      <span class="np-badge" data-np-badge>0</span>
      <i class="bi bi-chevron-down np-caret"></i>
    </button>
    <div class="np-wrap">
      <div class="np-inner">
        <div class="np-list" data-np-list></div>
      </div>
    </div>
  </div>`;

/* ── Helfer ── */
function dateKey(iso) { return new Date(iso).toISOString().slice(0, 10); }
function fmtDay(iso) {
  return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" });
}
function fmtTime(iso) {
  return new Date(iso).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
}
function relTime(iso) {
  const days = Math.floor((Date.now() - new Date(iso)) / 86400000);
  if (days <= 0) return "heute";
  if (days === 1) return "gestern";
  if (days < 7) return `vor ${days} T.`;
  const w = Math.floor(days / 7);
  if (w < 5) return `vor ${w} Wo.`;
  return `vor ${Math.floor(days / 30)} Mon.`;
}
function initials(name) {
  const p = String(name || "").replace(",", "").trim().split(/\s+/);
  return ((p[0]?.[0] || "") + (p[1]?.[0] || "")).toUpperCase();
}

function valPill(value, role) {
  const f = fmtValue(value);
  const cls = "np-val " + (f.kind === "empty" ? "empty" : role);
  return `<span class="${cls}">${esc(f.text)}</span>`;
}

function diffRow(diff) {
  const unternehmen = stammdatenService.unternehmensobjekt;
  const crumbs = buildCrumbs(diff.path, unternehmen);
  const leaf = crumbs[crumbs.length - 1];
  const ancestors = crumbs.slice(0, -1);
  const fullPath = crumbs.map(c => c.label + (c.index ? " " + c.index : "")).join(" › ");
  const crumbHtml = ancestors.length
    ? `<span class="np-crumb">${ancestors.map(c => esc(c.label) + (c.index ? " " + c.index : "")).join('<i class="bi bi-chevron-right"></i>')}<i class="bi bi-chevron-right"></i></span>`
    : "";
  return `
    <div class="np-diff">
      <div class="np-field" title="${esc(fullPath)}">${crumbHtml}${esc(leaf.label)}${leaf.index ? " " + leaf.index : ""}</div>
      <div class="np-change">
        ${valPill(resolveValue(diff.path, diff.oldValue, unternehmen, basedataService.beschaeftigungsarten), "old")}
        <span class="np-arrow"><i class="bi bi-arrow-right"></i></span>
        ${valPill(resolveValue(diff.path, diff.value, unternehmen, basedataService.beschaeftigungsarten), "new")}
      </div>
    </div>`;
}

function entryHtml(doc) {
  const diffs = (doc.fieldDiffs || []).map(diffRow).join("");
  return `
    <div class="np-entry">
      <div class="np-ehead">
        <span class="np-time">${fmtTime(doc.Datum)} Uhr</span>
        <span class="np-rel">· ${relTime(doc.Datum)}</span>
        <span class="np-user" title="${esc(doc.User || "")}">${initials(doc.User)}</span>
      </div>
      ${diffs}
    </div>`;
}

/* ════════════════════════════════════════════════════════════════════
   setupProtokoll(rootEl)
   Verdrahtet den Auslöser (Aufklappen + localStorage) und liefert ein
   Objekt mit render(docs) zurück.

     const protokoll = setupProtokoll(this.todo);
     protokoll.render(arrayAusCollection);
   ════════════════════════════════════════════════════════════════════ */
export function setupProtokoll(rootEl) {
  const root   = rootEl.querySelector("[data-protokoll]");
  const btn    = rootEl.querySelector("[data-np-toggle]");
  const label  = rootEl.querySelector("[data-np-label]");
  const listEl = rootEl.querySelector("[data-np-list]");
  const badge  = rootEl.querySelector("[data-np-badge]");

  const apply = (open) => {
    root.classList.toggle("open", open);
    btn.setAttribute("aria-expanded", String(open));
    if (label) label.textContent = open ? "Änderungsprotokoll" : "Änderungsprotokoll anzeigen";
  };

  // immer eingeklappt starten
  apply(false);
  btn.addEventListener("click", () => {
    apply(!root.classList.contains("open"));
  });

  function render(docs) {
    const entries = (docs || [])
      .map(d => ({ ...d, fieldDiffs: (d.fieldDiffs || []).filter(istSichtbar) }))
      .filter(d => d.fieldDiffs.length)
      .sort((a, b) => new Date(b.Datum) - new Date(a.Datum));
    const total = entries.reduce((s, e) => s + (e.fieldDiffs ? e.fieldDiffs.length : 0), 0);
    if (badge) badge.textContent = total;

    if (!entries.length) {
      listEl.innerHTML = `
        <div class="np-empty-state">
          <i class="bi bi-clock-history"></i>
          <span>Noch keine Änderungen protokolliert.</span>
        </div>`;
      return;
    }

    const groups = [];
    for (const e of entries) {
      const k = dateKey(e.Datum);
      let g = groups.find(x => x.key === k);
      if (!g) { g = { key: k, iso: e.Datum, items: [] }; groups.push(g); }
      g.items.push(e);
    }
    listEl.innerHTML = groups.map(g => `
      <div class="np-day"><i class="bi bi-calendar3"></i>${fmtDay(g.iso)}</div>
      ${g.items.map(e => entryHtml(e)).join("")}
    `).join("");
  }

  return { render };
}
